import {
  Flex,
  FlexColumn,
  P,
  TextareaInput,
  Button,
  useIsUserAuth,
  UserRoles,
  useFormConfigurationContext,
  useUserHasRole,
} from '@equifinance/shared';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { formatDateTime, formatUserName } from '@equifinance/shared/utils';
import { Task, useTasksContext } from '../../../../contexts/tasks';

const WrittenEvidenceHeaderTitle = styled.p`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[2]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const WrittenEvidenceSection = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[2]};
`}`;

const EvidenceItem = styled(Flex)`${({ theme }) => `
  line-height: ${theme.fontSize[3]};

  &:last-of-type {
    margin-bottom: ${theme.padding[2]};
  }
`}`;

const EvidenceInfo = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[4]};
  align-items: flex-start;
  justify-content: space-between;
  width: ${theme.buttonSubmitWidth};
  padding: ${theme.padding[3]} ${theme.padding[4]};
  background: ${theme.colours.neutral[8]};
  border-top-left-radius: ${theme.borderRadius[2]};
  border-bottom-left-radius: ${theme.borderRadius[2]};
  font-family: ${theme.fontFamily[1]};
  font-weight: ${theme.fontWeight.normal};
`}`;

const EvidenceCreator = styled(P)`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
`}`;

const EvidenceDateTime = styled(P)`${({ theme }) => `
  color: ${theme.colours.neutral[3]};
`}`;

const EvidenceContent = styled(P)`${({ theme }) => `
  padding: ${theme.padding[3]} ${theme.padding[4]};
  flex: 1;
  background: ${theme.colours.neutral[9]};
  border-top-right-radius: ${theme.borderRadius[2]};
  border-bottom-right-radius: ${theme.borderRadius[2]};
  color: ${theme.colours.neutral[2]};
`}`;

const WrittenEvidenceInput = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[5]};
`}`;

const PostButton = styled(Button)`${({ theme }) => `
  height: ${theme.smallButtonHeight};
  min-width: ${theme.commisionInputWidth};
  padding: ${theme.padding[2]} ${theme.padding[5]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bolder};
  line-height: ${theme.fontSize[3]};
  margin-left: auto;
`}`;

export function WrittenEvidence({ task }: { task: Task }) {
  const [writtenEvidenceText, setWrittenEvidenceText] = useState('');
  const { createWrittenEvidence } = useTasksContext();
  const { isDisabled } = useFormConfigurationContext();

  const isBroker = useIsUserAuth();
  const isSalesSupport = useUserHasRole(UserRoles.SALES_SUPPORT);
  const isUnderwriter = useUserHasRole(UserRoles.UNDERWRITER);

  const evidenceHidden = useMemo(
    () => (
      (isUnderwriter && ['Draft', 'Approved', 'ApprovedWithException'].includes(task.status))
      || ((isBroker || isSalesSupport) && ['Draft', 'PendingSubmission', 'SubmittedToLender', 'Approved', 'ApprovedWithException'].includes(task.status))),
    [task.status, isUnderwriter, isSalesSupport, isBroker],
  );

  const onPostComment = () => {
    if (!writtenEvidenceText) return;
    createWrittenEvidence({
      writtenEvidenceText,
      taskId: task.id,
    });
    setWrittenEvidenceText('');
  };

  return (
    <WrittenEvidenceSection>
      <WrittenEvidenceHeaderTitle>Written evidence to support this task</WrittenEvidenceHeaderTitle>
      <WrittenEvidenceSection>
        {task.writtenEvidence?.map((evidence) => (
          <EvidenceItem key={evidence.id}>
            <EvidenceInfo>
              <FlexColumn>
                <EvidenceCreator>{formatUserName(evidence.evidenceFromUserName)}</EvidenceCreator>
                <EvidenceDateTime>{formatDateTime(evidence.moment, 'DD/MM/YYYY, HH:mm:ss')}</EvidenceDateTime>
              </FlexColumn>
            </EvidenceInfo>
            <EvidenceContent>
              {evidence?.text}
            </EvidenceContent>
          </EvidenceItem>
        ))}
      </WrittenEvidenceSection>
      {
        !evidenceHidden && (
          <WrittenEvidenceInput>
            <TextareaInput
              id="Evidence-content"
              required
              rows={3}
              value={writtenEvidenceText}
              onChange={setWrittenEvidenceText}
            />
            <PostButton disabled={isDisabled || !writtenEvidenceText} onClick={onPostComment}>Post Comment</PostButton>
          </WrittenEvidenceInput>
        )
      }
    </WrittenEvidenceSection>
  );
}
